import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../../schema.dexie";
import { getConfig, getCriancaAtiva } from "../../db/queries";
import { diasAtras } from "../../lib/format";
import { filaRotinaCorporal } from "../domain";
import { confirmarMomentoComPasso, descartarMomento, getMomentosPendentes } from "../queries";
import {
  PassoParticipacao,
  type AtividadePsicomotora,
  type MomentoPsicomotor,
} from "../types";
import { ParticipacaoSelector } from "../ui/ParticipacaoSelector";
import { AtividadeFoto } from "../ui/AtividadeFoto";
import { IndicadorParticipacao } from "../ui/IndicadorParticipacao";

/**
 * Gingado · Hoje — a ação primária (registrar momento) a um toque, lembrete de
 * rotina corporal (P0.6), confirmação das escolhas da criança (P0.3) e panorama do
 * repertório. Espelha a tela Hoje dos módulos alimentar e musical.
 */
export function HojeCorpo() {
  const nav = useNavigate();
  const crianca = useLiveQuery(getCriancaAtiva);
  const cfg = useLiveQuery(getConfig);
  const atividades = useLiveQuery(() => db.atividade.toArray()) ?? [];
  const momentos = useLiveQuery(() => db.momento_psm.toArray()) ?? [];
  const pendentes = useLiveQuery(getMomentosPendentes) ?? [];

  const cadencia = cfg?.gingado_rotina_dias ?? 5;
  const retomar = filaRotinaCorporal(atividades, momentos, cadencia);

  const ativas = atividades.filter((a) => !a.arquivado);
  const nFavoritas = ativas.filter((a) => a.status === "favorita").length;
  const nExploracao = ativas.filter((a) => a.status === "em_exploracao").length;
  const nSensiveis = ativas.filter((a) => a.status === "sensivel").length;

  const ativDe = (id: string) => atividades.find((a) => a.id === id) ?? null;

  return (
    <main className="tela">
      <div className="row-entre">
        <h1 className="tela-titulo">
          Gingado{crianca ? ` · ${crianca.apelido}` : ""}
        </h1>
        <Link to="/gingado/config" className="btn btn-fantasma" aria-label="configurações">
          ⚙️
        </Link>
      </div>

      {ativas.length === 0 && (
        <div className="card boas-vindas">
          <div className="bv-titulo">um caderno de campo do corpo 🤸</div>
          <p className="bv-sub">como funciona, em 3 passos:</p>
          <ol className="bv-passos">
            <li>
              tenha atividades na <Link to="/gingado/biblioteca">biblioteca</Link>{" "}
              (já vem um repertório inicial).
            </li>
            <li>
              <strong>registre o momento</strong>: toque o passo de participação (0–9).
            </li>
            <li>
              veja a <strong>evolução</strong> e descubra o que acalma e o que ativa.
            </li>
          </ol>
          <p className="bv-rodape">organiza dados — não substitui o psicomotricista / TO.</p>
        </div>
      )}

      <button
        className="btn btn-primario btn-bloco btn-grande"
        style={{ marginBottom: "var(--s4)" }}
        onClick={() => nav("/gingado/registrar")}
      >
        🤸 registrar momento de corpo
      </button>

      <button
        className="btn btn-bloco btn-grande"
        style={{ marginBottom: "var(--s5)" }}
        onClick={() => nav("/gingado/crianca")}
      >
        🧒 modo escolha da criança
      </button>

      {ativas.length > 0 && (
        <section className="panorama">
          <Link to="/gingado/biblioteca?status=favorita" className="stat stat-seguro">
            <span className="stat-num">{nFavoritas}</span>
            <span className="stat-rot">favoritas</span>
          </Link>
          <Link to="/gingado/biblioteca?status=em_exploracao" className="stat stat-teste">
            <span className="stat-num">{nExploracao}</span>
            <span className="stat-rot">em exploração</span>
          </Link>
          <Link to="/gingado/biblioteca?status=sensivel" className="stat">
            <span className="stat-num">{nSensiveis}</span>
            <span className="stat-rot">sensíveis</span>
          </Link>
        </section>
      )}

      {ativas.length > 0 && (
        <Link to="/gingado/kit" className="btn btn-bloco" style={{ marginBottom: "var(--s5)" }}>
          🧰 kit rápido — acalmar / ativar / favoritas
        </Link>
      )}

      {/* P0.3 — confirmar escolhas da criança: o cuidador marca o passo observado */}
      {pendentes.length > 0 && (
        <section className="secao">
          <h2 className="secao-titulo">escolhas da criança pra confirmar</h2>
          {pendentes.map((m) => (
            <ConfirmarEscolha key={m.id} momento={m} atividade={ativDe(m.atividade_id)} />
          ))}
        </section>
      )}

      {/* P0.6 — lembrete de rotina corporal */}
      <section className="secao">
        <h2 className="secao-titulo">que tal retomar hoje?</h2>
        {retomar.length === 0 ? (
          <div className="card">
            <span style={{ color: "var(--text-muted)" }}>nada pendente — tudo em dia 🤸</span>
          </div>
        ) : (
          retomar.slice(0, 5).map(({ atividade, dias_desde_ultimo }) => (
            <button
              className="card linha-alimento"
              key={atividade.id}
              onClick={() => nav(`/gingado/registrar/${atividade.id}`)}
            >
              <AtividadeFoto
                midias={atividade.midias}
                nome={atividade.nome}
                tipo={atividade.tipo}
              />
              <div className="crescer">
                <div className="nome">{atividade.nome}</div>
                <div className="meta">{diasAtras(dias_desde_ultimo)}</div>
                <IndicadorParticipacao atividadeId={atividade.id} momentos={momentos} />
              </div>
              <span aria-hidden>🤸</span>
            </button>
          ))
        )}
      </section>
    </main>
  );
}

/**
 * P0.3 — confirmação de uma escolha da criança. A escolha nasce pendente no passo
 * "exploração" (placeholder); aqui o cuidador marca o passo de participação que de
 * fato observou antes de confirmar — é o dado clínico real, não um chute do sistema.
 */
function ConfirmarEscolha({
  momento,
  atividade,
}: {
  momento: MomentoPsicomotor;
  atividade: AtividadePsicomotora | null;
}) {
  const [passo, setPasso] = useState<PassoParticipacao>(momento.passo_participacao);

  return (
    <div className="card">
      <div className="row">
        <AtividadeFoto
          midias={atividade?.midias}
          nome={atividade?.nome}
          tipo={atividade?.tipo}
        />
        <div className="crescer">
          <div style={{ fontWeight: 500 }}>{atividade?.nome ?? "atividade"}</div>
          <div className="meta" style={{ color: "var(--text-muted)" }}>
            a criança escolheu · marque a participação que você observou
          </div>
        </div>
      </div>

      <div style={{ marginTop: "var(--s3)" }}>
        <ParticipacaoSelector valor={passo} onChange={setPasso} />
      </div>

      <div className="row" style={{ marginTop: "var(--s3)" }}>
        <button
          className="btn btn-primario crescer"
          onClick={() => confirmarMomentoComPasso(momento.id, passo)}
        >
          confirmar
        </button>
        <button className="btn" onClick={() => descartarMomento(momento.id)}>
          descartar
        </button>
      </div>
    </div>
  );
}
